import React from 'react';

export const PAYMENT_METHOD_ICONS = [
  {
    id: 'usdt-bsc',
    name: 'USDT (BSC / BEP20)',
    symbol: 'USDT',
    category: 'Crypto',
    network: 'BEP20',
    description: 'Tether on BNB Smart Chain'
  },
  {
    id: 'usdt-eth',
    name: 'USDT (ETH / ERC20)',
    symbol: 'USDT',
    category: 'Crypto',
    network: 'ERC20',
    description: 'Tether on Ethereum Network'
  },
  {
    id: 'usdc-bsc',
    name: 'USDC (BSC / BEP20)',
    symbol: 'USDC',
    category: 'Crypto',
    network: 'BEP20',
    description: 'USD Coin on BNB Smart Chain'
  },
  {
    id: 'usdc-eth',
    name: 'USDC (ETH / ERC20)',
    symbol: 'USDC',
    category: 'Crypto',
    network: 'ERC20',
    description: 'USD Coin on Ethereum Network'
  },
  {
    id: 'ltc',
    name: 'Litecoin (LTC)',
    symbol: 'LTC',
    category: 'Crypto',
    network: 'LTC Network',
    description: 'Litecoin Mainnet (Live rate gated)'
  },
  {
    id: 'amazon',
    name: 'Amazon Gift Card',
    symbol: 'Amazon',
    category: 'Gift Card',
    network: '',
    description: 'Amazon Gift Card / Voucher'
  },
  {
    id: 'paypal',
    name: 'PayPal',
    symbol: 'PayPal',
    category: 'PayPal',
    network: '',
    description: 'PayPal Invoice or Direct Transfer'
  },
  {
    id: 'btc',
    name: 'Bitcoin (BTC)',
    symbol: 'BTC',
    category: 'Crypto',
    network: 'BTC Network',
    description: 'Bitcoin On-chain'
  },
  {
    id: 'binance',
    name: 'Binance Pay',
    symbol: 'Binance',
    category: 'Crypto',
    network: 'Binance Pay',
    description: 'Binance Pay ID / Pay QR'
  },
  {
    id: 'card',
    name: 'Credit / Debit Card',
    symbol: 'Card',
    category: 'E-Wallet',
    network: '',
    description: 'Card or E-Wallet Gateway'
  },
  {
    id: 'bank',
    name: 'Bank / UPI',
    symbol: 'Bank',
    category: 'Other',
    network: '',
    description: 'Bank Wire / UPI / Direct Transfer'
  },
  {
    id: 'custom',
    name: 'Custom Icon',
    symbol: 'Custom',
    category: 'Other',
    network: '',
    description: 'Custom Image URL or generic'
  }
];

export const resolveIconKey = (method) => {
  if (!method) return 'card';
  const explicit = method.iconKey || method.icon;
  if (explicit && explicit !== 'custom') return explicit;

  const name = String(method.name || '').toLowerCase();
  const symbol = String(method.symbol || '').toUpperCase();
  const network = String(method.network || '').toUpperCase();
  const category = String(method.category || '').toLowerCase();

  // USDT
  if (name.includes('usdt') || symbol === 'USDT' || name.includes('tether')) {
    if (network.includes('ETH') || network.includes('ERC20') || name.includes('erc20') || name.includes('eth')) {
      return 'usdt-eth';
    }
    return 'usdt-bsc';
  }

  // USDC
  if (name.includes('usdc') || symbol === 'USDC') {
    if (network.includes('ETH') || network.includes('ERC20') || name.includes('erc20') || name.includes('eth')) {
      return 'usdc-eth';
    }
    return 'usdc-bsc';
  }

  // Litecoin
  if (name.includes('litecoin') || symbol === 'LTC' || name.includes('ltc')) {
    return 'ltc';
  }

  // Amazon
  if (name.includes('amazon') || category === 'gift card') {
    return 'amazon';
  }

  // PayPal
  if (name.includes('paypal') || symbol.includes('PAYPAL') || category === 'paypal') {
    return 'paypal';
  }

  // Bitcoin
  if (name.includes('bitcoin') || symbol === 'BTC' || name.includes('btc')) {
    return 'btc';
  }

  // Binance
  if (name.includes('binance') || symbol.includes('BNB') || network.includes('BINANCE')) {
    return 'binance';
  }

  if (category === 'crypto') return 'btc';
  if (category === 'e-wallet') return 'card';

  return 'card';
};

const SIZE_MAP = {
  xs: 'w-6 h-6',
  sm: 'w-8 h-8',
  md: 'w-10 h-10',
  lg: 'w-12 h-12',
  xl: 'w-14 h-14'
};

export const PaymentMethodIcon = ({
  method = null,
  iconKey = null,
  iconUrl = null,
  size = 'md',
  className = '',
  showNetworkBadge = true
}) => {
  const finalUrl = iconUrl || method?.iconUrl;
  const key = iconKey || resolveIconKey(method);
  const sizeClasses = SIZE_MAP[size] || SIZE_MAP.md;

  if (finalUrl) {
    return (
      <div className={`relative inline-flex shrink-0 items-center justify-center rounded-xl overflow-hidden bg-gray-100 dark:bg-gray-800 shadow-sm border border-gray-200/60 dark:border-gray-700/60 ${sizeClasses} ${className}`}>
        <img src={finalUrl} alt={method?.name || key} className="w-full h-full object-contain p-1" />
      </div>
    );
  }

  const renderIconGraphic = () => {
    switch (key) {
      case 'usdt-bsc':
      case 'usdt-eth':
        return (
          <div className="relative w-full h-full flex items-center justify-center bg-gradient-to-br from-[#26A17B] to-[#167054] text-white shadow-inner">
            <svg viewBox="0 0 32 32" fill="none" className="w-3/4 h-3/4">
              <path d="M16 2.5C8.54 2.5 2.5 8.54 2.5 16S8.54 29.5 16 29.5 29.5 23.46 29.5 16 23.46 2.5 16 2.5z" fill="#1B8B67" opacity="0.4" />
              <path d="M17.8 13.6v-1.7h4.8V8.9H9.4v3h4.8v1.7C9.3 13.8 6 14.8 6 16c0 1.2 3.3 2.2 8.2 2.4V25h3.6v-6.6c4.9-.2 8.2-1.2 8.2-2.4 0-1.2-3.3-2.2-8.2-2.4zm0 3.7c-3.7.2-6.9-.5-7.6-1.3.7-.8 3.9-1.5 7.6-1.3v2.6zm4-.1c-.7.8-3.9 1.5-7.6 1.3V16c3.7-.2 6.9.5 7.6 1.2z" fill="#FFFFFF" />
            </svg>
            {showNetworkBadge && (
              <span className={`absolute bottom-0 right-0 px-1 py-[1px] text-[7.5px] font-black rounded-tl-md shadow-xs leading-none ${
                key === 'usdt-bsc' ? 'bg-[#F0B90B] text-black font-extrabold' : 'bg-[#627EEA] text-white'
              }`}>
                {key === 'usdt-bsc' ? 'BSC' : 'ETH'}
              </span>
            )}
          </div>
        );

      case 'usdc-bsc':
      case 'usdc-eth':
        return (
          <div className="relative w-full h-full flex items-center justify-center bg-gradient-to-br from-[#2775CA] to-[#1A4F8B] text-white shadow-inner">
            <svg viewBox="0 0 32 32" fill="none" className="w-3/4 h-3/4">
              <circle cx="16" cy="16" r="13" fill="#2060A8" opacity="0.4" />
              <path d="M16 6c-5.5 0-10 4.5-10 10s4.5 10 10 10 10-4.5 10-10S21.5 6 16 6zm.3 16.5v1.4h-1.6v-1.4c-2.5-.2-3.9-1.6-3.9-3.3h2.3c.1.9.9 1.5 2 1.5 1.2 0 1.9-.6 1.9-1.5 0-1.1-.9-1.4-2.4-1.8-2.4-.6-3.8-1.4-3.8-3.2 0-1.7 1.4-3 3.6-3.2v-1.4h1.6v1.4c2.2.2 3.5 1.4 3.7 3h-2.2c-.2-.8-.7-1.3-1.7-1.3-1 0-1.7.5-1.7 1.3 0 1 .9 1.3 2.4 1.7 2.5.6 3.8 1.5 3.8 3.3 0 1.7-1.4 3.1-4 3.3z" fill="#FFFFFF" />
            </svg>
            {showNetworkBadge && (
              <span className={`absolute bottom-0 right-0 px-1 py-[1px] text-[7.5px] font-black rounded-tl-md shadow-xs leading-none ${
                key === 'usdc-bsc' ? 'bg-[#F0B90B] text-black font-extrabold' : 'bg-[#627EEA] text-white'
              }`}>
                {key === 'usdc-bsc' ? 'BSC' : 'ETH'}
              </span>
            )}
          </div>
        );

      case 'ltc':
        return (
          <div className="relative w-full h-full flex items-center justify-center bg-gradient-to-br from-[#345D9D] via-[#244273] to-[#172B4D] text-white shadow-inner">
            <svg viewBox="0 0 32 32" fill="none" className="w-3/4 h-3/4">
              <circle cx="16" cy="16" r="13" fill="#1B335A" opacity="0.5" />
              <path d="M12.8 7.5h3.3l-2.6 9.2 3.1-.9-.6 1.9-3.7 1-1.3 4.8h9.2l-.7 2.2H11.2l5.4-18.2z" fill="#FFFFFF" />
            </svg>
            {showNetworkBadge && (
              <span className="absolute bottom-0 right-0 px-1 py-[1px] text-[7.5px] font-black rounded-tl-md bg-sky-400 text-slate-950 shadow-xs leading-none">
                LTC
              </span>
            )}
          </div>
        );

      case 'amazon':
        return (
          <div className="relative w-full h-full flex items-center justify-center bg-gradient-to-br from-[#131921] to-[#232F3E] text-white shadow-inner">
            <svg viewBox="0 0 32 32" fill="none" className="w-3/4 h-3/4">
              <path d="M14.5 15.8c-1.8 0-3.1.4-3.1 1.7 0 1.8 1.9 1.6 3.1 1v-2.7zm2.2 4.4h-1.9v-.7c-.7.6-1.5 1-2.6 1-1.9 0-3.4-1.1-3.4-3 0-2.3 2-3.1 4.1-3.1h1.8v-.7c0-.8-.5-1.4-1.7-1.4-1 0-1.9.4-2.4.8l-.6-1.4c.8-.6 2-1 3.5-1 2.3 0 3.3 1.2 3.3 3.3v6.2z" fill="#FFFFFF" />
              <path d="M8.2 21.8c3.2 2 8.3 2.3 13.1-.2.3-.2.6.2.3.5-4.2 3-9.8 2.9-13.6.3-.3-.2.1-.8.2-.6z" fill="#FF9900" />
              <path d="M21.5 21.1c.4-.3 1.2-.2 1.4.2.2.4-.3 1.1-.7 1.4-.4.3-1 .1-1.1-.3-.1-.5.1-1 .4-1.3z" fill="#FF9900" />
            </svg>
            {showNetworkBadge && (
              <span className="absolute bottom-0 right-0 px-1 py-[1px] text-[7px] font-black rounded-tl-md bg-[#FF9900] text-black shadow-xs leading-none">
                GIFT
              </span>
            )}
          </div>
        );

      case 'paypal':
        return (
          <div className="relative w-full h-full flex items-center justify-center bg-gradient-to-br from-[#003087] via-[#00457C] to-[#0079C1] text-white shadow-inner">
            <svg viewBox="0 0 32 32" fill="none" className="w-3/4 h-3/4">
              <path d="M19 10.3c.7-1.1.6-2.1-.2-2.8-.9-.8-2.5-1-4.5-1h-5.2c-.6 0-1.1.4-1.2 1l-2.6 16.5c-.1.4.2.8.6.8h4.2l1-6.5c.1-.6.6-1 1.2-1h1.5c3.5 0 6.2-1.4 7-5.5.1-.6.1-1.1.2-1.5z" fill="#0079C1" />
              <path d="M21.2 12.8c-.8 4.1-3.5 5.5-7 5.5h-1.5c-.6 0-1.1.4-1.2 1l-1.3 8.2c-.1.4.2.8.6.8h3.8c.5 0 1-.4 1.1-.9l.8-5.1c.1-.5.5-.9 1.1-.9h.7c3 0 5.4-1.2 6.1-4.7.3-1.5.1-2.8-.5-3.9z" fill="#00457C" />
              <path d="M19 10.3c-.8 4.1-3.5 5.5-7 5.5h-1.5c-.6 0-1.1.4-1.2 1l1.3-8.2c.1-.6.6-1 1.2-1h1.5c2 0 3.6.2 4.5 1 .8.7.9 1.7.2 2.8z" fill="#FFFFFF" opacity="0.9" />
            </svg>
          </div>
        );

      case 'btc':
        return (
          <div className="relative w-full h-full flex items-center justify-center bg-gradient-to-br from-[#F7931A] to-[#C96F05] text-white shadow-inner">
            <svg viewBox="0 0 32 32" fill="none" className="w-3/4 h-3/4">
              <circle cx="16" cy="16" r="13" fill="#B86200" opacity="0.4" />
              <path d="M20.5 13.8c.3-1.8-1.1-2.7-3-3.4l.6-2.5-1.5-.4-.6 2.4c-.4-.1-.8-.2-1.2-.3l.6-2.4-1.5-.4-.6 2.5c-.3-.1-.7-.2-1-.2l-2.1-.5-.4 1.6s1.1.3 1.1.3c.6.2.7.7.6 1.1l-1.5 6c.1 0 .2.1.3.1-.1 0-.2-.1-.3-.1l-2.1 8.4c-.1.4-.4.7-.9.5 0 0-1.1-.3-1.1-.3l-.7 1.8 2 .5c.4.1.7.2 1.1.3l-.6 2.6 1.5.4.6-2.5c.4.1.8.2 1.2.3l-.6 2.5 1.5.4.6-2.5c2.7.5 4.7.3 5.5-2.1.7-2-.1-3.1-1.5-3.8 1.1-.3 1.9-1 2.1-2.5zm-3.8 5.6c-.5 2-3.9 1-5 1.2l.9-3.6c1.1.3 4.6.9 4.1 2.4zm.5-5.9c-.5 1.8-3.3.9-4.2 1.1l.8-3.3c.9.2 3.9.7 3.4 2.2z" fill="#FFFFFF" />
            </svg>
            {showNetworkBadge && (
              <span className="absolute bottom-0 right-0 px-1 py-[1px] text-[7.5px] font-black rounded-tl-md bg-amber-200 text-amber-950 shadow-xs leading-none">
                BTC
              </span>
            )}
          </div>
        );

      case 'binance':
        return (
          <div className="relative w-full h-full flex items-center justify-center bg-[#181A20] text-[#F0B90B] shadow-inner">
            <svg viewBox="0 0 32 32" fill="none" className="w-3/4 h-3/4">
              <path d="M16 5.5l3.8 3.8-3.8 3.8-3.8-3.8 3.8-3.8zM23.6 13.1l3.8 3.8-3.8 3.8-3.8-3.8 3.8-3.8zM16 20.7l3.8 3.8-3.8 3.8-3.8-3.8 3.8-3.8zM8.4 13.1l3.8 3.8-3.8 3.8-3.8-3.8 3.8-3.8zM16 13.1l3.8 3.8-3.8 3.8-3.8-3.8 3.8-3.8z" fill="#F0B90B" />
            </svg>
            {showNetworkBadge && (
              <span className="absolute bottom-0 right-0 px-1 py-[1px] text-[7.5px] font-black rounded-tl-md bg-[#F0B90B] text-black shadow-xs leading-none">
                PAY
              </span>
            )}
          </div>
        );

      case 'bank':
        return (
          <div className="relative w-full h-full flex items-center justify-center bg-gradient-to-br from-teal-600 to-cyan-700 text-white shadow-inner">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-3/5 h-3/5">
              <path d="m2 7 10-5 10 5v2H2z" />
              <path d="M4 11v7" />
              <path d="M8 11v7" />
              <path d="M12 11v7" />
              <path d="M16 11v7" />
              <path d="M20 11v7" />
              <path d="M2 20h20" />
            </svg>
          </div>
        );

      case 'card':
      default:
        return (
          <div className="relative w-full h-full flex items-center justify-center bg-gradient-to-br from-indigo-500 to-purple-600 text-white shadow-inner">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-3/5 h-3/5">
              <rect width="20" height="14" x="2" y="5" rx="3" />
              <line x1="2" x2="22" y1="10" y2="10" />
            </svg>
          </div>
        );
    }
  };

  return (
    <div className={`relative inline-flex shrink-0 items-center justify-center rounded-xl overflow-hidden shadow-xs border border-black/5 dark:border-white/10 ${sizeClasses} ${className}`}>
      {renderIconGraphic()}
    </div>
  );
};

export const PaymentMethodIconPicker = ({
  selectedKey = 'usdt-bsc',
  onSelectKey,
  onApplyPreset = null
}) => {
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 dark:text-gray-300">
          Payment Icon Selection
        </label>
        <span className="text-xs text-blue-600 dark:text-blue-400 font-semibold">
          Select icon or 1-click preset
        </span>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2.5 max-h-64 overflow-y-auto p-2 border border-gray-200 dark:border-gray-700 rounded-xl bg-gray-50/50 dark:bg-gray-800/40">
        {PAYMENT_METHOD_ICONS.map((item) => {
          const isSelected = selectedKey === item.id;
          return (
            <button
              key={item.id}
              type="button"
              onClick={() => {
                onSelectKey(item.id);
                if (onApplyPreset) {
                  onApplyPreset(item);
                }
              }}
              className={`flex items-center gap-2.5 p-2 rounded-xl text-left border transition-all duration-150 ${
                isSelected
                  ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30 ring-2 ring-blue-500/20 shadow-xs'
                  : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-blue-300 dark:hover:border-gray-600'
              }`}
            >
              <PaymentMethodIcon iconKey={item.id} size="sm" showNetworkBadge={true} />
              <div className="min-w-0 flex-1">
                <p className="text-xs font-bold text-gray-900 dark:text-white truncate leading-tight">
                  {item.name}
                </p>
                <p className="text-[10px] text-gray-500 dark:text-gray-400 truncate">
                  {item.network || item.category}
                </p>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default PaymentMethodIcon;
